import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { liftRow, restingRect, useFlip, type Lift } from '../lib/springDrag.ts'
import { invalidateResources } from '../lib/useResource.ts'
import { useNavigate } from 'react-router-dom'
import { currencySymbol, routeStores, type Store } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { useStores } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Icon } from '../components/Icon.tsx'
import { Sheet } from '../components/Sheet.tsx'
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  Paragraph,
  ScreenHeader,
  SelectRow,
  TextCol,
} from '../components/ui.tsx'
import { OfflineBanner } from '../components/bits.tsx'
import { DeskToolbar, Panel } from '../components/desk.tsx'
import { StoreForm } from '../components/StoreForm.tsx'

function offlineAware(err: unknown): string {
  return err instanceof HttpError && err.isOffline ? 'Obchody jde měnit jen se signálem.' : errorMessage(err)
}

function moved(order: readonly string[], id: string, index: number): string[] {
  const next = order.filter((x) => x !== id)
  next.splice(Math.max(0, Math.min(index, next.length)), 0, id)
  return next
}

export function StoresPage() {
  const s = useSignedIn()
  const navigate = useNavigate()
  const desktop = useDesktop()

  if (desktop) {
    return (
      <div className="desk-view-main desk-narrow">
        <DeskToolbar
          crumb="Skupina"
          title="Obchody na trase"
          sub={s.group.name}
          actions={<Button onClick={() => navigate('/skupina')}>Zpět na skupinu</Button>}
        />
        <Panel>
          <StoresEditor />
        </Panel>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <div className="page">
        <ScreenHeader title="Obchody na trase" subtitle={s.group.name} onBack={() => navigate('/skupina')} />
        <OfflineBanner />
        <main className="page-content">
          <StoresEditor />
        </main>
      </div>
    </div>
  )
}

/**
 * Obchody skupiny; poradi je trasa velkeho nakupu (razeni pretazenim jako Linktree).
 * compact = v panelu stranky skupiny na pocitaci, kde je hlavni akci pozvanka.
 */
export function StoresEditor({ compact = false }: { compact?: boolean | undefined }) {
  const s = useSignedIn()
  const g = s.group
  const stores = useStores(g.id, true)
  const [order, setOrder] = useState<string[] | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [editing, setEditing] = useState<Store | 'new' | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const drag = useRef<{ id: string; order: string[] } | null>(null)
  /** Drzena kopie radku (Pruzina); null s omezenym pohybem v systemu. */
  const lift = useRef<Lift | null>(null)
  const stopDrag = useRef<(() => void) | null>(null)
  const captureRows = useFlip(listRef, '[data-store-id]', 'data-store-id')

  useEffect(
    () => () => {
      stopDrag.current?.()
      lift.current?.destroy()
    },
    [],
  )

  const all = stores.res.state === 'ok' ? stores.res.data : []
  const active = routeStores(all)
  const archived = all.filter((store) => store.archived)
  const ids = order ?? active.map((store) => store.id)

  // Obchody skupiny ukazuje vic mist (bocni panel, novy nakup, trasa na strance Skupina)
  // a nektere jen aktivni — po zmene se nactou znovu vsechny.
  const storesChanged = () => invalidateResources(`sp:obchody:${g.id}:`)

  async function saveOrder(next: string[]) {
    if (next.join() === active.map((store) => store.id).join()) {
      setOrder(null)
      return
    }
    setOrder(next)
    setError(null)
    try {
      const updated = await api.stores.reorder(g.id, next)
      stores.setData((prev) => prev.map((store) => updated.find((u) => u.id === store.id) ?? store))
      storesChanged()
    } catch (err) {
      setError(offlineAware(err))
    } finally {
      setOrder(null)
    }
  }

  const saveLatest = useRef(saveOrder)
  saveLatest.current = saveOrder

  function startDrag(e: PointerEvent<HTMLButtonElement>, id: string) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    e.preventDefault()
    stopDrag.current?.()
    const pointerId = e.pointerId
    drag.current = { id, order: ids }
    lift.current?.destroy()
    const rowEl = e.currentTarget.closest<HTMLElement>('[data-store-id]')
    lift.current = rowEl ? liftRow(rowEl, e.clientX, e.clientY) : null
    setOrder(ids)
    setDragging(id)

    // Posluchace na okne, ne pointer capture na uchytu: React pri zmene poradi
    // radek v DOM presune, prohlizec capture zrusi a pri rychlem tahu uz pohyb
    // ani pusteni k uchytu nedojde — kopie zamrzla.
    const onMove = (ev: globalThis.PointerEvent) => {
      const current = drag.current
      if (ev.pointerId !== pointerId || !current || !listRef.current) return
      lift.current?.move(ev.clientX, ev.clientY)
      // Cilova pozice = kolik ostatnich radku ma stred nad prstem.
      const rows = [...listRef.current.querySelectorAll<HTMLElement>('[data-store-id]')].filter(
        (row) => row.dataset['storeId'] !== current.id,
      )
      const index = rows.filter((row) => {
        const rect = restingRect(row)
        return rect.top + rect.height / 2 < ev.clientY
      }).length
      if (current.order.indexOf(current.id) === index) return
      current.order = moved(current.order, current.id, index)
      captureRows()
      setOrder(current.order)
    }
    const onEnd = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      const current = drag.current
      cleanup()
      if (current) void saveLatest.current(current.order)
      const landed = lift.current
      lift.current = null
      if (current) {
        landed?.drop(
          () => listRef.current?.querySelector<HTMLElement>(`[data-store-id="${CSS.escape(current.id)}"]`) ?? null,
        )
      } else {
        landed?.destroy()
      }
    }
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      drag.current = null
      stopDrag.current = null
      setDragging(null)
    }
    stopDrag.current = cleanup
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, id: string) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    captureRows()
    const index = ids.indexOf(id)
    void saveOrder(moved(ids, id, e.key === 'ArrowUp' ? index - 1 : index + 1))
  }

  async function setArchived(store: Store, value: boolean) {
    setError(null)
    try {
      const updated = await api.stores.update(store.id, { archived: value })
      stores.setData((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      storesChanged()
      setEditing(null)
    } catch (err) {
      setError(offlineAware(err))
    }
  }

  return (
    <>
      <Paragraph size={compact ? 'meta' : 'body'}>
        Pořadí určuje trasu nákupu s víc obchody. Když pojedeš jinudy, přetáhni obchody do pořadí, v jakém je navštívíš.
      </Paragraph>

      <div ref={listRef} className="stack-8">
        {ids.map((id, index) => {
          const store = active.find((x) => x.id === id)
          if (!store) return null
          return (
            <SelectRow
              key={id}
              data-store-id={id}
              pad="grip"
              className={`store-row${dragging === id ? ' is-dragging' : ''}`}
            >
              <button
                type="button"
                className="grip"
                aria-label={`Přesunout ${store.name}, ${index + 1}. na trase. Šipkami nahoru a dolů.`}
                onPointerDown={(e) => startDrag(e, id)}
                onKeyDown={(e) => onKeyDown(e, id)}
              >
                <Icon name="grip" size={20} />
              </button>
              {compact && <span className="num-badge">{index + 1}</span>}
              <TextCol title={store.name} sub={store.city ?? undefined} />
              <span className="currency-pill">{currencySymbol(store.currency)}</span>
              <IconButton icon="more" tone="muted" label={`Upravit ${store.name}`} onClick={() => setEditing(store)} />
            </SelectRow>
          )
        })}
      </div>

      {stores.res.state === 'ok' && active.length === 0 && (
        <EmptyState title="Zatím žádný obchod">
          Přidej obchody, kam jezdíte. Nákup podle nich rozdělí seznam a ceny v jejich měnách.
        </EmptyState>
      )}
      {stores.res.state === 'error' && <Paragraph tone="error">{stores.res.message}</Paragraph>}
      {error && <Paragraph tone="error">{error}</Paragraph>}

      {compact ? (
        <Button icon="plus" className="self-start" onClick={() => setEditing('new')}>
          Přidat obchod
        </Button>
      ) : (
        <Button kind="primary" icon="plus" full onClick={() => setEditing('new')}>
          Přidat obchod
        </Button>
      )}

      {!compact && (
        <Card gap="sm">
          <span className="card-title">Obchody patří skupině</span>
          <Paragraph>Každá skupina má svoje. Nákup obchod mít nemusí – stačí „kdekoli“.</Paragraph>
        </Card>
      )}

      {archived.length > 0 && (
        <>
          <button
            type="button"
            className="disclosure"
            aria-expanded={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            <span>Skryté obchody ({archived.length})</span>
            <Icon name={showArchived ? 'chevron_up' : 'chevron_down'} size={20} />
          </button>
          {showArchived &&
            archived.map((store) => (
              <SelectRow key={store.id} pad="even">
                <TextCol title={store.name} sub={store.city ?? undefined} />
                <Button compact onClick={() => void setArchived(store, false)}>
                  Obnovit
                </Button>
              </SelectRow>
            ))}
        </>
      )}

      <Sheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Přidat obchod' : 'Upravit obchod'}
        subtitle={g.name}
      >
        {editing !== null && (
          <div className="stack">
            <StoreForm
              key={editing === 'new' ? 'new' : editing.id}
              groupId={g.id}
              stores={all}
              store={editing === 'new' ? undefined : editing}
              onSaved={(saved) => {
                stores.setData((prev) =>
                  prev.some((x) => x.id === saved.id) ? prev.map((x) => (x.id === saved.id ? saved : x)) : [...prev, saved],
                )
                storesChanged()
                setEditing(null)
              }}
            />
            {editing !== 'new' && (
              <>
                <Paragraph size="meta">Skrytý obchod zmizí z trasy a z výběrů. Staré nákupy zůstanou beze změny.</Paragraph>
                <Button kind="danger" compact className="self-start" onClick={() => void setArchived(editing, true)}>
                  Skrýt obchod
                </Button>
              </>
            )}
          </div>
        )}
      </Sheet>
    </>
  )
}
